import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, type NormalizedCacheObject } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { useMemo } from 'react';

import { clientGraphqlEndpoint, graphqlEndpoint } from '../config/configuration';
import { getAccessToken } from '../utils/auth';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const httpLink = new HttpLink({
  uri: typeof window === 'undefined' ? graphqlEndpoint : clientGraphqlEndpoint,
  credentials: 'same-origin',
});

const getAuthHeaders = () => {
  const token = getAccessToken();

  if (!token) {
    return;
  }

  return {
    Authorization: `Bearer ${token}`,
  };
};

const authLink = setContext(async (operation, { headers, noAuth }) => {
  if (noAuth) {
    return {
      headers,
    };
  }

  return {
    headers: {
      ...headers,
      ...getAuthHeaders(),
    },
  };
});

export function initApolloClient() {
  return (apolloClient ??= new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([authLink, errorLink, httpLink]),
    cache: new InMemoryCache(),
  }));
}

export function useApolloClient(): ApolloClient<NormalizedCacheObject> {
  return useMemo(() => initApolloClient(), []);
}
